// decision-groups.js — makes the Coordination feed read one row per chat
// session instead of one row per file.
//
// New installs write one session record per chat (lease_id "session:<id>").
// Older history holds a per-file auto-stub for every lease a chat released
// over the time threshold — a dozen rows saying the same thing. Consecutive
// stubs from the same chat fold into one expandable row here; the records on
// disk are untouched.

// isSessionRecord: a record written once per chat session.
export function isSessionRecord(row) {
  return String(row.lease_id || "").startsWith("session:");
}

// isPerFileStub: a machine-written record about one released lease.
export function isPerFileStub(row) {
  return !!row.generated && !isSessionRecord(row);
}

// sessionFileCount reads the "N file(s) touched" line of a session record.
export function sessionFileCount(body) {
  const m = /(\d+) file\(s\) touched/.exec(String(body || ""));
  return m ? Number(m[1]) : 0;
}

function plural(n, word) {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

// groupDecisionRows takes decision rows sorted newest first, each with
// { agent_id, lease_id, generated, purpose, path, ts, ... }, and returns the
// same list with every run of 2+ consecutive per-file auto-stubs from one
// chat replaced by a single group row:
//   { group: true, count, items: [the folded rows], summary, ts, ... }
// Session records and person-written records are never folded.
export function groupDecisionRows(rows) {
  const out = [];
  let run = [];
  const flush = () => {
    if (run.length === 1) out.push(run[0]);
    else if (run.length > 1) out.push(groupRow(run));
    run = [];
  };
  for (const r of rows) {
    if (isPerFileStub(r) && run.length && run[0].agent_id === r.agent_id) {
      run.push(r);
      continue;
    }
    flush();
    if (isPerFileStub(r)) run.push(r);
    else out.push(r);
  }
  flush();
  return out;
}

function groupRow(items) {
  const newest = items[0];
  const purposes = [...new Set(items.map((r) => r.purpose).filter(Boolean))];
  const areas = [...new Set(items.map((r) => r.subsystem).filter(Boolean))];
  const what = purposes.length
    ? purposes[0] + (purposes.length > 1 ? ` (+${purposes.length - 1} more)` : "")
    : "";
  return {
    ...newest,
    group: true,
    count: items.length,
    items,
    lease_id: "group:" + newest.lease_id,
    subsystem: areas.length === 1 ? areas[0] : plural(areas.length, "area"),
    summary: plural(items.length, "file") + (what ? " · " + what : ""),
  };
}
